import React, { createContext, useContext, useState, useEffect } from 'react';

interface RealTimeData {
  treasury: {
    totalValue: number;
    suiStaked: number;
    usdcReserve: number;
    horseAssets: number;
    apy: number;
    dailyYield: number;
    monthlyYield: number;
  };
  horses: {
    totalHorses: number;
    activeHorses: number;
    racingToday: number;
    totalWinnings: number;
    nextRace: string;
  };
  governance: {
    activeProposals: number;
    totalProposals: number;
    participationRate: number;
    votingPower: number;
  };
  members: {
    totalMembers: number;
    activeMembers: number;
    newMembersToday: number;
    averageInvestment: number;
  };
  system: {
    uptime: number;
    securityScore: number;
    lastUpdate: Date;
  };
}

interface RealTimeDataContextType {
  data: RealTimeData;
  isConnected: boolean;
  lastUpdate: Date;
}

const RealTimeDataContext = createContext<RealTimeDataContextType | undefined>(undefined);

export const useRealTimeData = () => {
  const context = useContext(RealTimeDataContext);
  if (context === undefined) {
    throw new Error('useRealTimeData must be used within a RealTimeDataProvider');
  }
  return context;
};

export const RealTimeDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<RealTimeData>({
    treasury: {
      totalValue: 7.2,
      suiStaked: 4.2,
      usdcReserve: 1.8,
      horseAssets: 1.2,
      apy: 15.7,
      dailyYield: 2847,
      monthlyYield: 86500
    },
    horses: {
      totalHorses: 12,
      activeHorses: 10,
      racingToday: 2,
      totalWinnings: 125000,
      nextRace: '2024-02-15T15:30:00Z'
    },
    governance: {
      activeProposals: 3,
      totalProposals: 24,
      participationRate: 89.3,
      votingPower: 247
    },
    members: {
      totalMembers: 247,
      activeMembers: 221,
      newMembersToday: 3,
      averageInvestment: 12500
    },
    system: {
      uptime: 99.97,
      securityScore: 98.5,
      lastUpdate: new Date()
    }
  });

  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setData(prevData => {
        const now = new Date();
        const timeVariation = Math.sin(now.getTime() / 10000) * 0.1;
        const randomVariation = (Math.random() - 0.5) * 0.05;

        return {
          treasury: {
            ...prevData.treasury,
            totalValue: Math.max(7.0, prevData.treasury.totalValue + timeVariation + randomVariation),
            apy: Math.max(15.0, Math.min(16.5, prevData.treasury.apy + (Math.random() - 0.5) * 0.1)),
            dailyYield: Math.max(2500, prevData.treasury.dailyYield + (Math.random() - 0.5) * 100),
            monthlyYield: Math.max(80000, prevData.treasury.monthlyYield + (Math.random() - 0.5) * 2000)
          },
          horses: {
            ...prevData.horses,
            totalWinnings: prevData.horses.totalWinnings + (Math.random() * 1000),
            racingToday: Math.max(0, Math.min(5, prevData.horses.racingToday + Math.floor((Math.random() - 0.5) * 2)))
          },
          governance: {
            ...prevData.governance,
            participationRate: Math.max(85, Math.min(95, prevData.governance.participationRate + (Math.random() - 0.5) * 2)),
            votingPower: Math.max(240, prevData.governance.votingPower + Math.floor((Math.random() - 0.5) * 2))
          },
          members: {
            ...prevData.members,
            totalMembers: Math.max(240, prevData.members.totalMembers + Math.floor((Math.random() - 0.5) * 2)),
            activeMembers: Math.max(200, prevData.members.activeMembers + Math.floor((Math.random() - 0.5) * 3)),
            newMembersToday: Math.max(0, prevData.members.newMembersToday + Math.floor((Math.random() - 0.5) * 2))
          },
          system: {
            ...prevData.system,
            uptime: Math.max(99.9, Math.min(100, prevData.system.uptime + (Math.random() - 0.5) * 0.01)),
            securityScore: Math.max(95, Math.min(100, prevData.system.securityScore + (Math.random() - 0.5) * 0.5)),
            lastUpdate: now
          }
        };
      });
      setLastUpdate(new Date());
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Simulate connection status
  useEffect(() => {
    const connectionInterval = setInterval(() => {
      // 95% chance of being connected
      setIsConnected(Math.random() > 0.05);
    }, 10000);

    return () => clearInterval(connectionInterval);
  }, []);

  return (
    <RealTimeDataContext.Provider value={{
      data,
      isConnected,
      lastUpdate
    }}>
      {children}
    </RealTimeDataContext.Provider>
  );
};
